import React, { useState, useEffect } from "react";
import { User, Award, X, ChevronDown } from "lucide-react";
import axios from "axios";
import { GrGallery } from "react-icons/gr";
import UploadImg from "../Cloudinary/UploadImg";

const AddCertificatesModal = ({ onClose, onSubmit, existingData }) => {
  const [formData, setFormData] = useState({
    name: "",
    certificateType: "",
    certificateName: "",
    description: "",
    certificateImage: [], // Keep as array for consistency
  });

  const ISOCertificate = [
    { name: "ISO 9001:2015" },
    { name: "ISO 14001:2015" },
    { name: "ISO 45001:2018" },
    { name: "ISO 22000:2018" },
    { name: "ISO 27001:2013" },
    { name: "ISO 50001:2011" },
    { name: "ISO 13485:2016" },
    { name: "ISO 29990:2010" },
    { name: "ISO 20000-1:2018" },
  ];

  const ComplianceCertificate = [
    { name: "GMP Compliance" },
    { name: "IATF 16949:2016 Compliance" },
    { name: "KOSHER Compliance" },
    { name: "HACCP Compliance" },
    { name: "CE Compliance" },
    { name: "SA 8000 Compliance" },
    { name: "ORGANIC Compliance" },
    { name: "ROHS Compliance" },
    { name: "HALAL Compliance" },
  ];

  const [selectedCertificateTypes, setSelectedCertificateTypes] = useState([]);
  const [selectedSpecificCertificates, setSelectedSpecificCertificates] =
    useState([]);
  const [showDropdowns, setShowDropdowns] = useState({
    "ISO Certificate": false,
    "Compliance Certificate": false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pre-fill form with existing data
  useEffect(() => {
    if (existingData) {
      setFormData({
        name: existingData.name || "",
        certificateType: existingData.certificateType || "",
        certificateName: existingData.certificateName || "",
        description: existingData.description || "",
        // Handle both single image and array
        certificateImage: Array.isArray(existingData.certificateImage)
          ? existingData.certificateImage
          : existingData.certificateImage
          ? [existingData.certificateImage]
          : [],
      });

      if (existingData.certificateType) {
        const types = existingData.certificateType
          .split(",")
          .map((t) => t.trim());
        setSelectedCertificateTypes(types);
      }

      if (existingData.certificateName) {
        const names = existingData.certificateName
          .split(",")
          .map((n) => n.trim());
        setSelectedSpecificCertificates(names);

        const newShowDropdowns = { ...showDropdowns };
        names.forEach((certName) => {
          if (ISOCertificate.some((cert) => cert.name === certName)) {
            newShowDropdowns["ISO Certificate"] = true;
          }
          if (ComplianceCertificate.some((cert) => cert.name === certName)) {
            newShowDropdowns["Compliance Certificate"] = true;
          }
        });
        setShowDropdowns(newShowDropdowns);
      }
    }
  }, [existingData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Upload file using your Cloudinary UploadImg component
      const uploadedImageUrl = await UploadImg(file);
      console.log("uploadedImageUrl======>", uploadedImageUrl);

      // Update formData with the new image
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          certificateImage: [...prev.certificateImage, uploadedImageUrl],
        };
        // Log the updated data here using the newFormData
        console.log("Updated formData=====>", newFormData);
        return newFormData;
      });

      setSuccess("Image uploaded successfully!");
    } catch (err) {
      console.error("Image upload error:", err);
      setError("Failed to upload image. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateTypeChange = (type) => {
    setSelectedCertificateTypes((prev) => {
      const newTypes = prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type];

      if (prev.includes(type)) {
        const certificatesToRemove =
          type === "ISO Certificate"
            ? ISOCertificate.map((cert) => cert.name)
            : ComplianceCertificate.map((cert) => cert.name);

        setSelectedSpecificCertificates((prevSpecific) =>
          prevSpecific.filter((cert) => !certificatesToRemove.includes(cert))
        );

        setShowDropdowns((prevShow) => ({ ...prevShow, [type]: false }));
      } else {
        setShowDropdowns((prevShow) => ({ ...prevShow, [type]: true }));
      }

      return newTypes;
    });
  };

  const handleSpecificCertificateChange = (certificateName) => {
    setSelectedSpecificCertificates((prev) =>
      prev.includes(certificateName)
        ? prev.filter((item) => item !== certificateName)
        : [...prev, certificateName]
    );
  };

  const toggleDropdown = (type) => {
    setShowDropdowns((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare the data object
      const data = {
        name: formData.name,
        certificateType: selectedCertificateTypes.join(", "),
        certificateName: selectedSpecificCertificates.join(", "),
        description: formData.description,
        // Handle image properly - use first image or empty string
        certificateImage:
          formData.certificateImage.length > 0 ? formData.certificateImage : [],
      };

      console.log("Final submit data====>", data);

      // Validation
      if (!data.name.trim()) {
        setError("Name is required");
        return;
      }

      if (selectedCertificateTypes.length === 0) {
        setError("Please select at least one certificate type");
        return;
      }

      if (selectedSpecificCertificates.length === 0) {
        setError("Please select at least one specific certificate");
        return;
      }

      if (!data.description.trim()) {
        setError("Description is required");
        return;
      }

      if (existingData?._id) {
        // Update existing certificate
        if (onSubmit) await onSubmit(data);
        setSuccess("Certificate updated successfully!");
      } else {
        // Create new certificate
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/certificate/create-certificate`,
          data
        );

        console.log("API Response===========>", response.data);
        setSuccess("Certificate(s) added successfully!");

        // Reset form
        setFormData({
          name: "",
          certificateType: "",
          certificateName: "",
          description: "",
          certificateImage: [],
        });
        setSelectedCertificateTypes([]);
        setSelectedSpecificCertificates([]);
        setShowDropdowns({
          "ISO Certificate": false,
          "Compliance Certificate": false,
        });
      }

      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        err.response?.data?.message || "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getCertificateOptions = (type) =>
    type === "ISO Certificate" ? ISOCertificate : ComplianceCertificate;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-w-3xl w-full mx-auto bg-white shadow-xl rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div>
          {success && (
            <div className="p-3 bg-green-100 text-green-700 rounded-lg text-sm mx-6 mt-4">
              {success}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm mx-6 mt-4">
              {error}
            </div>
          )}

          {/* Header */}
          <div className="py-1 bg-gradient-to-r from-[#0070a8] to-[#023955] shadow-lg relative">
            <p className="text-center text-xl md:text-2xl font-normal p-2 text-white">
              {existingData ? "Edit Certificate" : "Add Certificate"}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 md:p-8">
            <div className="max-w-2xl mx-auto space-y-5">
              {/* Name */}
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border border-gray-200">
                <User size={18} className="text-gray-400" />
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Your Name"
                  required
                  className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              {/* Certificate Types */}
              <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
                <div className="flex gap-2 items-center mb-4">
                  <Award size={20} className="text-blue-500" />
                  <h2 className="text-gray-500 text-sm md:text-base">
                    Certificate Types
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {["ISO Certificate", "Compliance Certificate"].map((cert) => (
                    <label
                      key={cert}
                      className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer p-3 rounded-lg border border-transparent hover:bg-white hover:border-gray-300"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={selectedCertificateTypes.includes(cert)}
                        onChange={() => handleCertificateTypeChange(cert)}
                      />
                      <span>{cert}</span>
                    </label>
                  ))}
                </div>

                {selectedCertificateTypes.map((certType) => (
                  <div key={certType} className="mb-4">
                    <div className="mb-2">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(certType)}
                        className="w-full flex items-center justify-between p-3 bg-white border border-gray-300 rounded-lg hover:border-blue-400 transition-colors"
                      >
                        <span className="text-sm text-gray-700">
                          Select {certType}s (
                          {
                            selectedSpecificCertificates.filter((cert) =>
                              getCertificateOptions(certType).some(
                                (option) => option.name === cert
                              )
                            ).length
                          }{" "}
                          selected)
                        </span>
                        <ChevronDown
                          size={16}
                          className={`transform transition-transform ${
                            showDropdowns[certType] ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {showDropdowns[certType] && (
                      <div className="bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                        {getCertificateOptions(certType).map((option) => (
                          <label
                            key={option.name}
                            className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4"
                              checked={selectedSpecificCertificates.includes(
                                option.name
                              )}
                              onChange={() =>
                                handleSpecificCertificateChange(option.name)
                              }
                            />
                            <span className="text-sm text-gray-700">
                              {option.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Selected Certificates */}
              {selectedSpecificCertificates.length > 0 && (
                <div className="bg-blue-50 rounded-xl px-4 py-3 border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">
                    Selected Certificates ({selectedSpecificCertificates.length}
                    )
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedSpecificCertificates.map((cert) => (
                      <span
                        key={cert}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                      >
                        {cert}
                        <button
                          type="button"
                          onClick={() => handleSpecificCertificateChange(cert)}
                          className="hover:text-blue-600"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="rounded-lg bg-gray-50 px-3 py-2 md:py-3 flex items-start gap-3 border border-gray-200">
                <textarea
                  name="description"
                  rows="5"
                  placeholder="Description"
                  required
                  className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-400 text-sm md:text-base resize-none"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              {/* Image Upload */}
              <div>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border hover:border-[#0070a8] transition-all duration-200">
                  <GrGallery size={18} className="text-gray-400" />
                  <input
                    type="file"
                    name="certificateImage"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="bg-transparent outline-none w-full text-gray-700 text-sm md:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {/* Preview uploaded images */}
                {formData.certificateImage.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {formData.certificateImage.map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img}
                          alt="Certificate"
                          className="h-20 w-20 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              certificateImage: prev.certificateImage.filter(
                                (_, i) => i !== index
                              ),
                            }))
                          }
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gradient-to-r from-[#333333] to-[#cccccc] hover:from-[#cccccc] hover:to-[#333333] text-white font-medium py-2 rounded-lg text-sm shadow-md transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white font-medium py-2 rounded-lg text-sm shadow-md transition-all duration-300"
                >
                  {loading
                    ? existingData
                      ? "Updating..."
                      : "Adding..."
                    : existingData
                    ? "Update"
                    : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCertificatesModal;
